import { motion } from "framer-motion";
import { useRef } from "react";
import { useScroll, useTransform } from "framer-motion";
import { EXPERIENCE, SITE } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

function ClassroomImage() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [-28, 28]);
    return (
        <motion.figure
            ref={ref}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mt-12"
        >
            <div className="overflow-hidden rounded-xl border border-[#E5E1D8]">
                <motion.img
                    style={{ y, scale: 1.14 }}
                    src={SITE.experienceImage}
                    alt="Vinayak presenting to a cohort"
                    data-testid="experience-image"
                    className="h-64 w-full object-cover sm:h-80"
                />
            </div>
            <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C8B88]">
                in the classroom — presenting to the cohort
            </figcaption>
        </motion.figure>
    );
}

export default function Experience() {
    return (
        <section
            id="experience"
            className="border-t border-[#E5E1D8]/70 bg-[#F4F0E8]/40"
        >
            <div className="mx-auto max-w-4xl px-6 py-24 sm:px-10 sm:py-32">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8C8B88]"
                >
                    experience
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
                    className="mt-6 font-serif text-3xl tracking-tight text-[#141413] sm:text-4xl"
                >
                    where the work happened
                </motion.h2>
                <div className="mt-10 border-t border-[#E5E1D8]/80">
                    {EXPERIENCE.map((job, i) => (
                        <motion.article
                            key={job.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.6, delay: i * 0.07, ease: EASE }}
                            data-testid={`experience-card-${job.id}`}
                            className="group grid gap-2 border-b border-[#E5E1D8]/80 py-8 transition-colors duration-300 hover:bg-[#FBF9F5]/60 sm:grid-cols-[11rem_1fr] sm:gap-8 sm:px-4"
                        >
                            <p className="font-mono text-xs leading-relaxed text-[#8C8B88] transition-colors duration-300 group-hover:text-[#C84B31]">
                                {job.years}
                                <br />
                                <span className="text-[#8C8B88]/70">
                                    {job.place}
                                </span>
                            </p>
                            <div>
                                <h3 className="font-serif text-xl tracking-tight text-[#141413] sm:text-2xl">
                                    {job.company}
                                </h3>
                                <p className="mt-1 text-sm font-medium text-[#6E6D6A]">
                                    {job.title}
                                </p>
                                <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#6E6D6A] sm:text-[15px]">
                                    {job.summary}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>
                <ClassroomImage />
            </div>
        </section>
    );
}
